import type { CSSProperties } from 'react';
import type { Theme, ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

/** Soft accent chips aligned with ThemeContext semantic tokens (settings hubs style). */
export type DashboardAccent = 'info' | 'success' | 'warning' | 'error' | 'primary';

export function dashboardAccentChipClass(colors: ThemeColors, accent: DashboardAccent = 'info') {
  switch (accent) {
    case 'success':
      return colors.success;
    case 'warning':
      return colors.warning;
    case 'error':
      return colors.error;
    case 'primary':
      return cn(colors.primary, colors.primaryText);
    case 'info':
    default:
      return colors.info;
  }
}

/** Stat / health widgets → theme accent mapping (replaces hardcoded gradients). */
export const dashboardStatAccents = {
  email: 'info',
  systemOk: 'success',
  todos: 'warning',
  appointments: 'error',
  clients: 'info',
  growth: 'success',
  disk: 'info',
  security: 'warning',
  users: 'primary',
} as const satisfies Record<string, DashboardAccent>;

/** @deprecated Use dashboardStatAccents + dashboardAccentChipClass */
export const dashboardStatGradients = {
  email: '',
  systemOk: '',
  todos: '',
  appointments: '',
  clients: '',
  growth: '',
  disk: '',
  security: '',
  users: '',
} as const;

export function changePillClass(
  colors: ThemeColors,
  variant: 'success' | 'warning' | 'neutral' = 'success',
) {
  const base = 'text-xs inline-flex w-fit max-w-full items-center gap-1 rounded-md px-2 py-1';
  if (variant === 'warning') return cn(base, colors.warning);
  if (variant === 'neutral') return cn(base, colors.textSecondary);
  return cn(base, colors.success);
}

export function statusToneClass(
  colors: ThemeColors,
  tone: 'good' | 'warn' | 'bad' | 'neutral',
): { shell: string; value: string } {
  switch (tone) {
    case 'good':
      return { shell: cn('border', colors.border, colors.success), value: '' };
    case 'warn':
      return { shell: cn('border', colors.border, colors.warning), value: '' };
    case 'bad':
      return { shell: cn('border', colors.border, colors.error), value: '' };
    case 'neutral':
    default:
      return {
        shell: cn('border', colors.border, colors.surfaceHover),
        value: colors.text,
      };
  }
}

export function destructiveIconBtnClass(colors: ThemeColors) {
  return cn(
    'rounded-lg transition-colors flex items-center justify-center',
    colors.error,
    'bg-transparent',
    colors.interactiveHover,
  );
}

export function chartGridStroke(theme: Theme): string {
  return theme === 'dark' ? '#374151' : '#e5e7eb';
}

export function gridDotStyle(theme: Theme): CSSProperties {
  const isDark = theme === 'dark';
  return {
    backgroundImage: isDark
      ? 'radial-gradient(circle at center, rgba(203, 213, 225, 0.4) 1px, transparent 1px)'
      : 'radial-gradient(circle at center, rgba(148, 163, 184, 0.5) 1px, transparent 1px)',
    backgroundSize: '34px 34px',
  };
}

export function filterBarClass(colors: ThemeColors) {
  return cn(
    'flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-0 px-3 py-2.5 sm:px-4 rounded-2xl border shadow-sm',
    colors.border,
    colors.surface,
  );
}

export function filterGroupClass(colors: ThemeColors) {
  return cn(
    'flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1 min-w-0 rounded-xl border px-4 py-3 sm:py-2.5',
    colors.border,
    colors.headerBg,
  );
}

export function filterLabelClass(colors: ThemeColors) {
  return cn('flex items-center gap-2 shrink-0 text-xs font-semibold uppercase tracking-wider', colors.textMuted);
}

export function filterDividerClass(colors: ThemeColors) {
  return cn('hidden sm:block w-px h-6 self-stretch sm:self-center', colors.border, 'border-l');
}

export function segmentedControlClass(colors: ThemeColors, compact = false) {
  return cn(
    'inline-flex items-center rounded-xl border shadow-sm',
    compact ? 'p-0.5' : 'p-0.5 sm:p-1',
    colors.border,
    colors.surface,
  );
}

export function segmentedOptionClass(
  colors: ThemeColors,
  selected: boolean,
  variant: 'solid' | 'soft' = 'solid',
  compact = false,
) {
  const base = cn(
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-150',
    compact ? 'px-2.5 py-1.5' : 'px-3 py-2 min-h-[36px] sm:min-h-0',
  );
  if (!selected) {
    return cn(base, colors.textSecondary, colors.interactiveHover);
  }
  if (variant === 'soft') {
    return cn(base, colors.primaryFg, colors.surfaceHover, 'shadow-sm');
  }
  return cn(base, colors.primary, colors.primaryText, 'shadow-sm');
}

export function toolbarBtnClass(colors: ThemeColors, active = false) {
  const base =
    'inline-flex items-center justify-center rounded-lg min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] p-2 transition-all duration-150 touch-manipulation';
  if (active) {
    return cn(base, colors.primary, colors.primaryText, 'shadow-md hover:opacity-90');
  }
  return cn(base, colors.textMuted, colors.interactiveHover);
}

export function actionBtnClass(colors: ThemeColors) {
  return cn(
    'p-2 rounded-lg transition-colors flex items-center justify-center min-h-[40px] min-w-[40px] sm:min-h-[36px] sm:min-w-[36px] touch-manipulation',
    colors.textMuted,
    colors.interactiveHover,
  );
}

export function widgetCardClass(colors: ThemeColors, embedded = false, padding = 'p-5') {
  if (embedded) return 'min-w-0 flex-1 flex flex-col min-h-0';
  return cn(colors.surface, 'rounded-xl shadow-sm border', colors.border, padding);
}

export function widgetCardClassLg(colors: ThemeColors) {
  return cn(colors.surface, 'rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 border', colors.border);
}

export function widgetTitleClass(colors: ThemeColors, size: 'base' | 'lg' = 'base') {
  return cn(size === 'lg' ? 'text-lg' : 'text-base', 'font-semibold', colors.text);
}

export function listRowClass(colors: ThemeColors, extra?: string) {
  return cn(
    'flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors border-b last:border-0',
    colors.borderLight,
    colors.interactiveHover,
    extra,
  );
}

export function listRowClassCenter(colors: ThemeColors, extra?: string) {
  return cn('flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors', colors.interactiveHover, extra);
}

export function viewAllLinkClass(colors: ThemeColors) {
  return cn('text-sm font-medium whitespace-nowrap transition-colors hover:opacity-80', colors.primaryFg);
}

export function widgetShellClass(colors: ThemeColors) {
  return cn(
    colors.surface,
    'rounded-2xl shadow-md shadow-black/5 hover:shadow-lg hover:shadow-black/5 transition-shadow duration-200 border overflow-hidden h-full w-full min-w-0 flex flex-col relative max-w-none',
    colors.border,
  );
}

export function switcherTriggerClass(colors: ThemeColors) {
  return cn(
    'inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 min-h-[44px] sm:min-h-0 text-sm font-medium transition-colors shadow-sm',
    colors.border,
    colors.surface,
    colors.text,
    colors.interactiveHover,
  );
}

export function dropdownPanelClass(colors: ThemeColors) {
  return cn(
    'absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border shadow-lg py-1 max-h-[min(60vh,320px)] overflow-y-auto',
    colors.border,
    colors.surface,
  );
}

export function dropdownItemClass(colors: ThemeColors, selected = false) {
  if (selected) {
    return cn(
      'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
      colors.primaryFg,
      colors.surfaceHover,
    );
  }
  return cn(
    'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
    colors.text,
    colors.interactiveHover,
  );
}

export function metricCardClass(colors: ThemeColors) {
  return cn(colors.surface, 'rounded-lg shadow-sm p-4 border hover:shadow-md transition-shadow', colors.border);
}

export function skeletonPulseClass(colors: ThemeColors, extra?: string) {
  return cn(colors.surfaceHover, 'animate-pulse rounded', extra);
}

export function progressTrackClass(colors: ThemeColors) {
  return cn('w-full rounded-full h-2 sm:h-3 overflow-hidden', colors.surfaceHover);
}

export function editControlsPanelClass(colors: ThemeColors) {
  return cn(
    'absolute top-2 right-2 z-[2] flex items-center gap-0.5 rounded-xl border shadow-lg backdrop-blur-sm py-1 pl-1.5 pr-1 sm:py-1 sm:pl-1.5 sm:pr-1',
    colors.border,
    colors.surface,
  );
}

export function emptyStateIconClass(colors: ThemeColors) {
  return cn('w-12 h-12 mx-auto mb-3 opacity-40', colors.textMuted);
}

export function sectionTitleClass(colors: ThemeColors) {
  return cn('text-sm font-semibold mb-4 px-1', colors.textSecondary);
}

export function modalOverlayClass() {
  return 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4';
}

export function modalPanelClass(colors: ThemeColors, wide = false) {
  return cn(
    'rounded-xl shadow-xl border max-h-[90vh] overflow-hidden flex flex-col',
    wide ? 'w-full max-w-2xl' : 'w-full max-w-lg',
    colors.surface,
    colors.border,
  );
}

export function primaryToolbarBtnClass(colors: ThemeColors, active = false) {
  const base = 'inline-flex items-center justify-center transition-colors border';
  if (active) {
    return cn(base, colors.primary, colors.primaryText, 'border-transparent hover:opacity-90');
  }
  return cn(base, colors.surface, colors.text, colors.border, colors.interactiveHover);
}

export function primaryActiveRowClass(colors: ThemeColors) {
  return cn(colors.surfaceHover, colors.primaryBorder);
}

export function shellAvatarClass(colors: ThemeColors) {
  return cn('rounded-full flex items-center justify-center flex-shrink-0', colors.primary, colors.primaryText);
}

export function dateChipClass(colors: ThemeColors) {
  return cn(
    'h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex flex-col items-center justify-center',
    colors.primary,
    colors.primaryText,
  );
}

export function primaryFocusRingClass(colors?: ThemeColors) {
  if (colors?.focusRingPrimary) {
    return cn('focus:outline-none focus:ring-2 focus:ring-offset-1', colors.focusRingPrimary);
  }
  return 'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 dark:focus:ring-indigo-400';
}
